import { EmployeeSchedule } from '@/helpers/workingPattern/employeeSchedule';
import { secondsSinceMidnightToTimeString, timeStringToSecondsSinceMidnight } from '../../../../../helpers/dateHelper';
import { DEFAULT_SPLIT_DURATION_SECONDS } from '../../splitAvailabilityDefaults';

export interface ContractBounds {
  earliestStartTime?: number;
  latestEndTime?: number;
}

export const getDaySchedule = (schedule: EmployeeSchedule[], dayNumber?: number) =>
  schedule
    .filter(item => item.dayNumber === dayNumber)
    .sort((left, right) => (left.slotNumber ?? 0) - (right.slotNumber ?? 0));

export const getSplitPlacement = (
  schedule: EmployeeSchedule[],
  dayNumber: number | undefined,
  contract: ContractBounds
): 'before' | 'after' | null => {
  const daySchedule = getDaySchedule(schedule, dayNumber);
  const firstSlot = daySchedule.find(item => (item.slotNumber ?? 0) === 0);

  if (daySchedule.length >= 2 || !firstSlot?.available || !firstSlot.start || !firstSlot.end) {
    return null;
  }

  const earliestStart = contract.earliestStartTime ?? 0;
  const latestEnd = contract.latestEndTime ?? 24 * 60 * 60;
  const currentStart = timeStringToSecondsSinceMidnight(firstSlot.start);
  const currentEnd = timeStringToSecondsSinceMidnight(firstSlot.end);
  const canAddBefore = currentStart > earliestStart;
  const canAddAfter = currentEnd < latestEnd;

  if (!canAddBefore && !canAddAfter) {
    return null;
  }

  return canAddAfter ? 'after' : 'before';
};

export const canAddSplitPeriod = (
  schedule: EmployeeSchedule[],
  dayNumber: number | undefined,
  contract: ContractBounds
): boolean => getSplitPlacement(schedule, dayNumber, contract) !== null;

export const buildSplitDefaults = (
  schedule: EmployeeSchedule[],
  scheduleDay: EmployeeSchedule,
  contract: ContractBounds
) => {
  const firstSlot = getDaySchedule(schedule, scheduleDay.dayNumber).find(item => (item.slotNumber ?? 0) === 0);
  const placement = getSplitPlacement(schedule, scheduleDay.dayNumber, contract) ?? 'after';
  const earliestStart = contract.earliestStartTime ?? 0;
  const latestEnd = contract.latestEndTime ?? 24 * 60 * 60;
  const currentStart = firstSlot?.start ? timeStringToSecondsSinceMidnight(firstSlot.start) : earliestStart;
  const currentEnd = firstSlot?.end ? timeStringToSecondsSinceMidnight(firstSlot.end) : latestEnd;

  if (placement === 'before') {
    const endSeconds = currentStart;
    const startSeconds = Math.max(earliestStart, endSeconds - DEFAULT_SPLIT_DURATION_SECONDS);

    return {
      start: secondsSinceMidnightToTimeString(startSeconds),
      end: secondsSinceMidnightToTimeString(endSeconds),
      placement
    };
  }

  const startSeconds = currentEnd;
  const endSeconds = Math.min(latestEnd, startSeconds + DEFAULT_SPLIT_DURATION_SECONDS);

  return {
    start: secondsSinceMidnightToTimeString(startSeconds),
    end: secondsSinceMidnightToTimeString(Math.max(endSeconds, startSeconds)),
    placement: 'after' as const
  };
};
